import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { api, type StocktakeSession } from '../api';
import { useAuth } from '../auth';
import { useLocations, useStocktakes } from '../queries';
import { Badge, Button, Card, Empty, ErrorMessage, PageHeader, Spinner, when } from '../ui';

/** Start a bin count, and see count history. */
export function Stocktaking() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const locations = useLocations();
  const history = useStocktakes();

  const start = useMutation({
    mutationFn: (locationId: number) =>
      api<{ session: StocktakeSession }>('/api/v1/stocktaking', { body: { locationId } }).then((r) => r.session),
    onSuccess: (session) => {
      void qc.invalidateQueries({ queryKey: ['locations'] });
      void qc.invalidateQueries({ queryKey: ['stocktakes'] });
      navigate(`/stocktaking/${session.id}`);
    },
  });

  const writable = user?.role === 'owner' || user?.role === 'staff';

  return (
    <>
      <PageHeader
        title="Stocktaking"
        subtitle="Check a physical bin against BrickLoop, save progress, and apply confirmed corrections."
      />
      <div className="space-y-5">
        <ErrorMessage error={start.error} />
        {locations.isPending ? (
          <Spinner />
        ) : locations.data && locations.data.length === 0 ? (
          <Empty>No bins yet.</Empty>
        ) : (
          <Card title="Count a bin">
            <div className="overflow-x-auto">
              <table className="data w-full text-sm">
                <thead>
                  <tr>
                    <th>Bin</th>
                    <th>Warehouse</th>
                    <th className="text-right">Pieces</th>
                    <th>Last full count</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {locations.data?.map((l) => (
                    <tr key={l.id}>
                      <td className="font-medium">{l.code}</td>
                      <td>{l.warehouse}</td>
                      <td className="text-right tabular-nums">{l.pieces}</td>
                      <td>{l.lastFullCountAt ? when(l.lastFullCountAt) : <span className="text-slate-400">Never</span>}</td>
                      <td className="text-right whitespace-nowrap">
                        {l.openSessionId ? (
                          <Button small variant="secondary" onClick={() => navigate(`/stocktaking/${l.openSessionId}`)}>
                            Resume count
                          </Button>
                        ) : writable ? (
                          <Button small onClick={() => start.mutate(l.id)} disabled={start.isPending}>
                            Count this bin
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        <Card title="Count history">
          {history.isPending ? (
            <Spinner />
          ) : !history.data || history.data.length === 0 ? (
            <Empty>No counts yet.</Empty>
          ) : (
            <div className="overflow-x-auto">
              <table className="data w-full text-sm">
                <thead>
                  <tr>
                    <th>Bin</th>
                    <th>State</th>
                    <th>Scope</th>
                    <th className="text-right">Added</th>
                    <th className="text-right">Removed</th>
                    <th>Started</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {history.data.map((s) => (
                    <tr key={s.id}>
                      <td className="font-medium">{s.capturedLabel}</td>
                      <td>
                        <Badge tone={s.state === 'applied' ? 'green' : s.state === 'counting' ? 'amber' : 'slate'}>{s.state}</Badge>
                      </td>
                      <td>{s.scope ?? '—'}</td>
                      <td className="text-right tabular-nums">{s.result?.piecesAdded ?? 0}</td>
                      <td className="text-right tabular-nums">{s.result?.piecesRemoved ?? 0}</td>
                      <td>{when(s.createdAt)}</td>
                      <td className="text-right">
                        <Link to={`/stocktaking/${s.id}`} className="text-sm font-medium text-slate-700 underline">
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
